/**
 * Google Connect Button
 *
 * A button that initiates the Google OAuth flow.
 * Shows connection status and handles the popup flow.
 * Automatically shows setup wizard if credentials aren't configured.
 */

import { useState, useCallback, useEffect } from 'react';
import { useGoogleConnection, useSaveGoogleConnection, useDeleteGoogleConnection } from '@business-assistant/shared';
import { GoogleSetupWizard } from './GoogleSetupWizard';
import { useTranslation } from '../../hooks';
import './GoogleConnectButton.css';

interface GoogleConnectButtonProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export function GoogleConnectButton({
  onSuccess,
  onError,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
}: GoogleConnectButtonProps) {
  const { t } = useTranslation();
  const { data: googleConnection } = useGoogleConnection();
  const saveConnection = useSaveGoogleConnection();
  const deleteConnection = useDeleteGoogleConnection();

  const isConnected = googleConnection?.isConnected ?? false;
  const googleEmail = googleConnection?.googleEmail;

  // Local state for ephemeral OAuth operations
  const [isConnecting, setIsConnecting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);

  // Check if credentials are configured on mount
  useEffect(() => {
    const checkConfiguration = async () => {
      try {
        const response = await fetch('/api/google/auth/configured');
        const data = await response.json();
        setIsConfigured(data.configured);
      } catch {
        // If server isn't running, assume not configured
        setIsConfigured(false);
      }
    };
    checkConfiguration();
  }, []);

  const handleConnect = useCallback(async () => {
    setLocalError(null);

    // Check configuration first
    if (isConfigured === false) {
      setShowWizard(true);
      return;
    }

    setIsConnecting(true);
    try {
      // Open OAuth popup and wait for result
      const { openGoogleOAuthPopup, getDefaultServiceStates } = await import('@business-assistant/shared');
      const result = await openGoogleOAuthPopup();

      // Convert services array to Record with active status
      const serviceStates = getDefaultServiceStates();
      for (const serviceId of result.services) {
        if (serviceStates[serviceId]) {
          serviceStates[serviceId].status = 'active';
        }
      }

      // Save the connection to database via GraphQL mutation
      await saveConnection.mutateAsync({
        isConnected: true,
        sessionId: result.sessionId,
        googleEmail: result.email,
        authorizedScopes: [],
        services: serviceStates,
        connectedAt: new Date().toISOString(),
      });

      onSuccess?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Connection failed');
      // Don't show error if user just closed the popup (cancelled)
      if (err.message === 'USER_CANCELLED') {
        // User cancelled - just reset state, no error
        setIsConnecting(false);
        return;
      }
      setLocalError(err.message);
      onError?.(err);
    } finally {
      setIsConnecting(false);
    }
  }, [onSuccess, onError, isConfigured, saveConnection]);

  const handleWizardComplete = useCallback(async () => {
    setIsConfigured(true);
    setShowWizard(false);
    // Automatically start OAuth flow after setup
    setIsConnecting(true);
    try {
      // Open OAuth popup and wait for result
      const { openGoogleOAuthPopup, getDefaultServiceStates } = await import('@business-assistant/shared');
      const result = await openGoogleOAuthPopup();

      // Convert services array to Record with active status
      const serviceStates = getDefaultServiceStates();
      for (const serviceId of result.services) {
        if (serviceStates[serviceId]) {
          serviceStates[serviceId].status = 'active';
        }
      }

      // Save the connection to database via GraphQL mutation
      await saveConnection.mutateAsync({
        isConnected: true,
        sessionId: result.sessionId,
        googleEmail: result.email,
        authorizedScopes: [],
        services: serviceStates,
        connectedAt: new Date().toISOString(),
      });

      onSuccess?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Connection failed');
      // Don't show error if user just closed the popup (cancelled)
      if (err.message === 'USER_CANCELLED') {
        // User cancelled - just reset state, no error
        setIsConnecting(false);
        return;
      }
      setLocalError(err.message);
      onError?.(err);
    } finally {
      setIsConnecting(false);
    }
  }, [onSuccess, onError, saveConnection]);

  const handleDisconnect = useCallback(async () => {
    setLocalError(null);

    try {
      await deleteConnection.mutateAsync();
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Disconnect failed');
      setLocalError(err.message);
      onError?.(err);
    }
  }, [deleteConnection, onError]);

  // When connected, show only disconnect button
  if (isConnected) {
    return (
      <button
        className="google-disconnect-btn"
        onClick={handleDisconnect}
        type="button"
      >
        {t('google.disconnect')}
      </button>
    );
  }

  return (
    <>
      <div className="google-connect-wrapper">
        <button
          className={`google-connect-btn google-connect-btn--${variant} google-connect-btn--${size} ${fullWidth ? 'google-connect-btn--full' : ''}`}
          onClick={handleConnect}
          disabled={isConnecting || isConfigured === null}
          type="button"
        >
          {isConnecting ? (
            <>
              <span className="google-connect-spinner" />
              <span>{t('google.connecting')}</span>
            </>
          ) : isConfigured === null ? (
            <>
              <span className="google-connect-spinner" />
              <span>{t('google.loading')}</span>
            </>
          ) : (
            <>
              <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>{isConfigured ? t('google.connectWithGoogle') : t('google.configureGoogle')}</span>
            </>
          )}
        </button>
        {localError && (
          <p className="google-connect-error">{localError}</p>
        )}
      </div>

      {/* Setup Wizard Modal */}
      <GoogleSetupWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onComplete={handleWizardComplete}
      />
    </>
  );
}

export default GoogleConnectButton;
